<template>
  <div id="app">
    <div v-if="showContent" class="wrapper" @drop="handleDrop">
      <NavBar :authed="isUserSetup" />
      <LanguageSwitcher />
      <WrapperLayout :has-sidebar="hasSidebar">
        <router-view>
          <BaseStateIndicator state="loading" />
        </router-view>
      </WrapperLayout>
      <BaseToast v-model="showToast" data-cy="global-feedback-message">
        <p>{{ toast.content }}</p>
      </BaseToast>
    </div>
    <BaseStateIndicator v-else state="loading" />
  </div>
</template>

<script>
import { BaseStateIndicator, BaseToast } from '@samknows/design-system';
import { EventBus } from '@samknows/utils';

import NavBar from '@/common/components/NavBar.vue';
import LanguageSwitcher from '@/common/components/LanguageSwitcher.vue';
import WrapperLayout from '@/common/components/WrapperLayout.vue';
import { checkIsUserSetup } from '@/common/services/account/user';
import { setSavePreferencesOnServerHook } from '@/common/services/account/userPreferences';
import { computed, defineComponent, onBeforeMount, ref, watch } from 'vue';
import { useRoute } from 'vue-router/composables';
import { useWhiteLabelling } from './common/composables/whitelabel';

export default defineComponent({
  metaInfo() {
    const whiteLabelData = window.whitelabelData;
    const whiteLabelledURL =
      whiteLabelData &&
      whiteLabelData.whitelabelled_url &&
      whiteLabelData.whitelabelled_url.includes('meqyas');

    const keyTitle = whiteLabelledURL
      ? 'defaultAppMeqyasTitle'
      : 'defaultAppTitle';
    const defaultAppTitle = this.$t(`common.${keyTitle}`);

    return {
      titleTemplate: `%s - ${defaultAppTitle}`
    };
  },

  components: {
    NavBar,
    LanguageSwitcher,
    WrapperLayout,
    BaseToast,
    BaseStateIndicator
  },

  setup() {
    const { setupBranding } = useWhiteLabelling();
    const route = useRoute();
    const isUserSetup = ref(false);
    const isPagePublic = route.meta.public;
    const isAppSetup = ref(false);

    const showContent = computed(() => {
      return isPagePublic || isAppSetup.value;
    });

    watch(route, () => {
      isUserSetup.value = checkIsUserSetup();
    });

    onBeforeMount(async () => {
      const inCypress = !!window.Cypress;
      if (inCypress) {
        document.body.classList.add('in-cypress');
      }
      isUserSetup.value = checkIsUserSetup();

      if (!isPagePublic && isUserSetup.value) {
        setSavePreferencesOnServerHook();
        await setupBranding();
      }
      isAppSetup.value = true;
    });

    return {
      showContent,
      isUserSetup
    };
  },

  data() {
    return {
      toast: {
        content: ''
      },
      showToast: false,
      navbarSearchFlag: false
    };
  },

  computed: {
    hasSidebar() {
      return this.$route.meta.hasSideBar;
    }
  },

  mounted() {
    EventBus.$on('toast:show', (toast) => {
      this.toast = toast;
      this.showToast = true;
    });
  },

  methods: {
    handleDrop(e) {
      /* eslint-disable no-empty */
      try {
        const data = JSON.parse(e.dataTransfer.getData('text'));

        if (data.data || data.component) {
          e.preventDefault();
        }
      } catch (err) {}
    }
  }
});
</script>
